import PasswordStrength from '@fnando/password_strength/app/assets/javascripts/password_strength';
import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import PasswordStrengthIndicator from 'dashboard/components/PasswordStrengthIndicator';
import EmptyRow from 'dashboard/layouts/FormLayout/EmptyRow';
import Form from 'dashboard/layouts/FormLayout/Form';
import Section from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import FormControlField from 'lib/interfaces/FormControlField';
import {
  areValuesMatching,
  isMaxLength,
  isPasswordStrong,
  isRequired,
  withMessage,
} from 'lib/utils/formValidation';

const FORM_NAME = 'userAcceptInvite';

interface FormUserAcceptInviteProps {
  emailAddress: string;
  password: string;
  mfaRequired: boolean;
}

const PasswordStrengthContainer = styled.div`
  margin-top: -${Spacing[2]};
`;

const FormUserAcceptInvite = ({
  handleSubmit,
  submitting,
  pristine,
  emailAddress,
  password,
  mfaRequired,
}: FormUserAcceptInviteProps & InjectedFormProps & PropsFromRedux) => {
  const validate = useMemo(() => {
    return {
      password: [
        withMessage('Please enter a new password', isRequired),
        withMessage(
          'Provided passwords must match',
          areValuesMatching(['password', 'passwordConfirmation']),
        ),
        withMessage(
          'A strong password is required. Please try a mix of letters, numbers, and symbols.',
          isPasswordStrong(emailAddress),
        ),
        withMessage('Please limit password to 50 characters.', isMaxLength(50)),
      ],
      firstName: withMessage('Please enter your first name', isRequired),
      lastName: withMessage('Please enter your last name', isRequired),
    };
  }, [emailAddress]);

  // We use null as the empty value to differentiate from a super weak password with a score of 0
  let strength: number | null = null;
  if (Boolean(emailAddress) && Boolean(password)) {
    strength = PasswordStrength.test(emailAddress, password).score;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Section cols={2}>
        <FormControlField
          component={InputField}
          label='First name'
          name='firstName'
          id='firstName'
          validate={validate.firstName}
          disabled={submitting}
          placeholder='First'
          autocomplete='fname'
          autoFocus={true}
        />
        <FormControlField
          component={InputField}
          label='Last name'
          name='lastName'
          id='lastName'
          validate={validate.lastName}
          disabled={submitting}
          placeholder='Last'
          autocomplete='lname'
        />
      </Section>

      <FormControlField
        component={InputField}
        label='Password'
        name='password'
        id='password'
        type='password'
        validate={validate.password}
        disabled={submitting}
        placeholder='New password'
      />

      <PasswordStrengthContainer>
        <PasswordStrengthIndicator strength={strength} />
      </PasswordStrengthContainer>

      <FormControlField
        component={InputField}
        label='Confirm password'
        name='passwordConfirmation'
        id='passwordConfirmation'
        type='password'
        validate={validate.password}
        disabled={submitting}
        placeholder='Confirm new password'
      />

      <EmptyRow />
      <Paragraph>
        By continuing you agree to the{' '}
        <a href='https://withpersona.com/legal/privacy-policy' target='_blank' rel='noreferrer'>
          Privacy Policy
        </a>{' '}
        and the{' '}
        <a href='https://withpersona.com/legal/terms-of-use' target='_blank' rel='noreferrer'>
          Terms of Use
        </a>
        .
      </Paragraph>
      <EmptyRow />

      <Button
        type='submit'
        data-test='form-user-accept-invite__submit'
        disabled={submitting || pristine}
        isPending={submitting}
      >
        {mfaRequired ? 'Next' : 'Create account'}
      </Button>
    </Form>
  );
};

const reduxFormWrapped = reduxForm<FormUserAcceptInviteData, FormUserAcceptInviteProps>({
  form: FORM_NAME,
  // This is a multi-step form where the latter steps rely on the former steps.
  // Note that this means the password is retained in Redux, so we need to
  // manually clear form state on submit.
  destroyOnUnmount: false,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormUserAcceptInvite);

export interface FormUserAcceptInviteData {
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
}

const connector = connect(
  (state) => ({
    password: selector(state, 'password') ?? '',
  }),
  null,
);
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect to form state to grab the current value of `password`, which
// the component uses to determine password strength.
const selector = formValueSelector(FORM_NAME);
export default connector(reduxFormWrapped);
